import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Square, Play, Pause, Clock, Volume2, Upload, CheckCircle, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';

interface LiveRecordingProps {
  onRecordingComplete: (result: { type: 'async_job'; jobId: string } | { type: 'audio_url'; audioUrl: string }) => void;
  sessionId: string;
  salesStage: string;
  onRecordingReady: (filename: string, url: string) => void;
}

interface RecordingState {
  isRecording: boolean;
  isPaused: boolean;
  duration: number;
  isUploading: boolean;
  isComplete: boolean;
  error: string | null;
  recordingUrl: string | null;
  recordingFilename: string | null;
}

export const LiveRecording: React.FC<LiveRecordingProps> = ({
  onRecordingComplete,
  sessionId,
  salesStage,
  onRecordingReady,
}) => {
  const [state, setState] = useState<RecordingState>({
    isRecording: false,
    isPaused: false,
    duration: 0,
    isUploading: false,
    isComplete: false,
    error: null,
    recordingUrl: null,
    recordingFilename: null,
  });

  const [audioLevel, setAudioLevel] = useState<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const durationRef = useRef<number>(0);

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Audio level monitoring
  const monitorAudioLevel = () => {
    if (!analyserRef.current || !audioContextRef.current) {
      return;
    }

    // Use time domain data for better audio level detection
    const dataArray = new Uint8Array(analyserRef.current.fftSize);
    analyserRef.current.getByteTimeDomainData(dataArray);

    // Calculate RMS (Root Mean Square) for better volume detection
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const normalizedValue = (dataArray[i] - 128) / 128; // Normalize to -1 to 1
      sum += normalizedValue * normalizedValue;
    }
    const rms = Math.sqrt(sum / dataArray.length);
    const level = Math.min(100, Math.max(5, rms * 100 * 20)); // Even higher sensitivity + minimum 5% for visibility test

    setAudioLevel(level);

    // Continue monitoring - don't depend on state, just check if MediaRecorder is active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      animationFrameRef.current = requestAnimationFrame(monitorAudioLevel);
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      setState(prev => ({ ...prev, error: null }));

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;

      // Set up audio analysis
      audioContextRef.current = new AudioContext();

      // Resume AudioContext if suspended (required by some browsers)
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 2048; // Larger FFT size for better resolution
      analyserRef.current.smoothingTimeConstant = 0.1; // Very low smoothing for responsive levels
      analyserRef.current.minDecibels = -90;
      analyserRef.current.maxDecibels = -10;

      source.connect(analyserRef.current);
      // IMPORTANT: Don't connect to destination (speakers) to avoid feedback

      // Set up MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        handleRecordingStop();
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setState(prev => ({ ...prev, error: 'Błąd nagrywania: ' + (event.error?.message || 'Nieznany błąd') }));
      };

      mediaRecorder.start(1000); // Collect data every 1 second

      setState(prev => ({
        ...prev,
        isRecording: true,
        isPaused: false,
        duration: 0,
      }));

      // Start timer
      timerRef.current = setInterval(() => {
        setState(prev => {
          durationRef.current = prev.duration + 1;
          return {
            ...prev,
            duration: prev.duration + 1,
          };
        });
      }, 1000);

      // Start audio monitoring
      setTimeout(() => {
        monitorAudioLevel();
      }, 100);

    } catch (error) {
      console.error('Error starting recording:', error);
      setState(prev => ({
        ...prev,
        error: 'Nie można uzyskać dostępu do mikrofonu. Sprawdź uprawnienia przeglądarki.',
      }));
    }
  };

  // Pause/Resume recording
  const togglePause = () => {
    if (!mediaRecorderRef.current) return;

    if (state.isPaused) {
      mediaRecorderRef.current.resume();
      setState(prev => ({ ...prev, isPaused: false }));
      monitorAudioLevel();
    } else {
      mediaRecorderRef.current.pause();
      setState(prev => ({ ...prev, isPaused: true }));
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
  };

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && state.isRecording) {
      mediaRecorderRef.current.stop();
      setState(prev => ({ ...prev, isRecording: false, isPaused: false }));
    }

    // Stop timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Stop audio monitoring
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Stop microphone stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, [state.isRecording]);

  // Handle recording completion
  const handleRecordingStop = async () => {
    if (chunksRef.current.length === 0) {
      // Only show error if recording was supposed to be long enough
      if (durationRef.current < 2) {
        setState(prev => ({ ...prev, error: 'Nagrywanie zostało przerwane zbyt wcześnie. Sprawdź uprawnienia do mikrofonu.' }));
      } else {
        setState(prev => ({ ...prev, error: 'Nie udało się nagrać audio. Spróbuj ponownie.' }));
      }
      return;
    }
    setState(prev => ({ ...prev, isUploading: true, error: null }));

    try {
      const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
      const formData = new FormData();
      formData.append('audio', audioBlob, `recording-${Date.now()}.webm`);
      formData.append('salesStage', salesStage);

      const response = await fetch('/api/audio/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      const result = await response.json();

      setState(prev => ({
        ...prev,
        isUploading: false,
        isComplete: true,
        recordingUrl: result.fileInfo.publicUrl,
        recordingFilename: result.fileInfo.filename,
      }));
      onRecordingReady(result.fileInfo.filename, result.fileInfo.publicUrl);

    } catch (error) {
      console.error('Error uploading recording for playback:', error);
      setState(prev => ({
        ...prev,
        isUploading: false,
        error: 'Nie udało się zapisać nagrania. Spróbuj ponownie.',
      }));
    }
  };

  

  // Reset component state
  const resetRecording = () => {
    setState({
      isRecording: false,
      isPaused: false,
      duration: 0,
      isUploading: false,
      isComplete: false,
      error: null,
      recordingUrl: null,
    });
    chunksRef.current = [];
    setAudioLevel(0);
    durationRef.current = 0;
  };

  // Cleanup on unmount only
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  if (state.isComplete) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20
                        border border-green-200 dark:border-green-800 rounded-2xl p-8 text-center">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600 dark:text-green-400" />
          <h3 className="text-xl font-semibold text-green-800 dark:text-green-200 mb-2">
            Nagranie gotowe
          </h3>
          <p className="text-green-600 dark:text-green-400 mb-4">
            Twoje nagranie jest gotowe do odsłuchania.
          </p>
          <p className="text-sm text-green-500 dark:text-green-300 mb-4">
            Czas nagrania: {formatDuration(state.duration)}
          </p>

          {/* Audio Player */}
          {state.recordingUrl && (
            <div className="mt-4">
              <audio
                controls
                src={state.recordingUrl}
                className="w-full max-w-md mx-auto"
                preload="metadata"
              >
                Twoja przeglądarka nie obsługuje odtwarzania audio.
              </audio>
            </div>
          )}
        </div>

        <button
          onClick={resetRecording}
          className="w-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700
                     text-gray-700 dark:text-gray-300 font-medium py-3 px-6 rounded-xl transition-all duration-200"
        >
          Nagraj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      

      {/* Recording Interface */}
      <div className="bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-950/20 dark:to-pink-950/20
                      border border-red-200 dark:border-red-800 rounded-3xl p-8">

        {/* Audio Level Visualization */}
        {(state.isRecording || state.isUploading) && (
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-2">
              <Volume2 className="w-4 h-4 text-red-600 dark:text-red-400" />
              <span className="text-sm text-red-600 dark:text-red-400 font-medium">
                Poziom dźwięku
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-green-500 to-red-500 h-2 rounded-full transition-all duration-100"
                style={{ width: `${audioLevel}%` }}
              />
            </div>
          </div>
        )}

        {/* Timer */}
        {state.duration > 0 && (
          <div className="flex items-center justify-center space-x-2 mb-6">
            <Clock className="w-5 h-5 text-red-600 dark:text-red-400" />
            <span className="text-2xl font-mono font-bold text-red-700 dark:text-red-300">
              {formatDuration(state.duration)}
            </span>
          </div>
        )}

        {/* Recording Status */}
        <div className="text-center mb-6">
          {state.isUploading ? (
            <div className="space-y-2">
              <Upload className="w-12 h-12 mx-auto text-blue-600 dark:text-blue-400 animate-bounce" />
              <p className="text-lg font-medium text-blue-700 dark:text-blue-300">
                Przesyłanie nagrania...
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Nagranie jest przesyłane na Google Drive
              </p>
            </div>
          ) : state.isRecording ? (
            <div className="space-y-2">
              <div className="w-12 h-12 mx-auto bg-red-500 rounded-full animate-pulse flex items-center justify-center">
                <Mic className="w-6 h-6 text-white" />
              </div>
              <p className="text-lg font-medium text-red-700 dark:text-red-300">
                {state.isPaused ? 'Nagrywanie wstrzymane' : 'Trwa nagrywanie...'}
              </p>
              <p className="text-sm text-red-600 dark:text-red-400">
                Mów normalnie do mikrofonu
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Mic className="w-12 h-12 mx-auto text-gray-400" />
              <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
                Gotowy do nagrywania
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Kliknij przycisk, aby rozpocząć nagrywanie rozmowy
              </p>
            </div>
          )}
        </div>

        {/* Recording Controls */}
        <div className="flex justify-center space-x-4">
          {!state.isRecording ? (
            <button
              onClick={startRecording}
              disabled={state.isUploading}
              className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400
                         text-white font-medium py-4 px-8 rounded-2xl transition-all duration-200
                         shadow-lg hover:shadow-xl disabled:cursor-not-allowed"
            >
              <Mic className="w-5 h-5" />
              <span>Rozpocznij nagrywanie</span>
            </button>
          ) : (
            <div className="flex space-x-3">
              <button
                onClick={togglePause}
                className="flex items-center space-x-2 bg-yellow-600 hover:bg-yellow-700
                           text-white font-medium py-3 px-6 rounded-xl transition-all duration-200"
              >
                {state.isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                <span>{state.isPaused ? 'Wznów' : 'Wstrzymaj'}</span>
              </button>

              <button
                onClick={stopRecording}
                className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700
                           text-white font-medium py-3 px-6 rounded-xl transition-all duration-200"
              >
                <Square className="w-4 h-4" />
                <span>Zakończ</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {state.error && (
        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800
                        rounded-2xl p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <p className="text-red-700 dark:text-red-400 font-medium">
              {state.error}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};